import { Component, DestroyRef, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { AsyncPipe, NgForOf } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { ofType } from '@ngrx/effects';
import { debounceTime, map, Observable, startWith, take } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { PreloaderColor } from '@enums/shared.enum';
import { UiAvatarComponent, UiPreloaderComponent } from '@ui';
import { OrganizationEffect } from '@effects/organization.effect';
import { AppState } from '../../../../../store/app.state';
import { UiSearchComponent } from '../../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiTruncatedHtmlComponent } from '../../../../../shared/ui-kit/ui-truncated-html/ui-truncated-html.component';
import { UserOrganizationDropDown } from '@models/organization.model';
import { OrganizationsActions } from '@states/organization/organization.actions-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { toDropdownOrganization } from '../switch-organization.util';
import {
  SwitchOrganizationSupportAccessModalComponent,
} from '../../../../../modals/switch-organization-support-access-modal/switch-organization-support-access-modal.component';

@Component({
  selector: 'app-switch-organization-support-search',
  standalone: true,
  imports: [
    AsyncPipe,
    NgForOf,
    ReactiveFormsModule,
    TranslatePipe,
    UiSearchComponent,
    UiAvatarComponent,
    UiTruncatedHtmlComponent,
    UiPreloaderComponent,
  ],
  templateUrl: './switch-organization-support-search.component.html',
  styleUrl: './switch-organization-support-search.component.scss',
})
export class SwitchOrganizationSupportSearchComponent implements OnInit {
  @Output() public readonly onMenuClosed: EventEmitter<void> = new EventEmitter<void>();

  public readonly searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  public readonly loaderColor = PreloaderColor;
  public organizations$: Observable<UserOrganizationDropDown[]>;
  public isSearching$: Observable<boolean>;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly store$: Store<AppState>,
    private readonly dialog: MatDialog,
    private readonly organizationEffects: OrganizationEffect,
  ) {}

  public ngOnInit(): void {
    // Clear any stale results from a previous time this menu was opened and show the loader
    // immediately, rather than flashing the last search's results before the fresh one arrives.
    this.store$.dispatch(OrganizationsActions.resetSearchedOrganizations());

    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(query => this.store$.dispatch(OrganizationsActions.searchOrganizations({ query })));

    this.organizations$ = this.store$.select(OrganizationSelectors.selectSearchedOrganizations).pipe(
      map(documents => documents.map(toDropdownOrganization)),
    );

    this.isSearching$ = this.store$.select(OrganizationSelectors.selectIsSearchingOrganizations);
  }

  public onSearchInput(query: string): void {
    this.searchControl.setValue(query);
  }

  public openSupportAccessModal(organization: UserOrganizationDropDown): void {
    this.onMenuClosed.emit();

    // Only ever show the "enter access code" modal once the email has actually been sent —
    // opening it eagerly would let the user try to type a code that never arrives.
    this.organizationEffects.getSupportAccessByOrgId$
      .pipe(ofType(OrganizationsActions.getSupportAccessByOrgIdSuccess), take(1))
      .subscribe(() => {
        this.dialog.open(SwitchOrganizationSupportAccessModalComponent, {
          width: '460px',
          data: { orgId: organization.id, orgName: organization.name },
        });
      });

    this.store$.dispatch(OrganizationsActions.resetGetSupportAccessByOrgIdState());
    this.store$.dispatch(OrganizationsActions.getSupportAccessByOrgId({ orgId: organization.id }));
  }
}
